/**
 * Herkomst van de bezoeker vastleggen (first-party, alleen in de browser).
 *
 * Google Ads telt alleen een conversie wanneer de bezoeker via een advertentie
 * binnenkwam (`gclid` / `gbraid` / `wbraid` in de URL). Door die klik-id en de
 * utm-parameters bij de eerste paginaweergave op te slaan, kunnen we bij een
 * boeking laten zien of het om Ads-verkeer gaat — ook wanneer de bezoeker via
 * de betaalpagina terugkomt en de URL-parameters weg zijn.
 *
 * Er wordt niets naar de server gestuurd: dit is uitsluitend voor de meting en
 * de debug-uitvoer in de browser.
 */

export type Attribution = {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
  landing_page?: string;
  first_seen?: string;
};

const KEY = "taxiritje-attribution";
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

function read(store: Storage | undefined): Attribution | null {
  try {
    const raw = store?.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Attribution;
    if (parsed.first_seen && Date.now() - Date.parse(parsed.first_seen) > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function write(value: Attribution) {
  const raw = JSON.stringify(value);
  try {
    window.sessionStorage.setItem(KEY, raw);
  } catch {
    /* geen opslag beschikbaar */
  }
  try {
    window.localStorage.setItem(KEY, raw);
  } catch {
    /* privémodus */
  }
}

/** Huidige herkomst, of `null` wanneer er niets bekend is. */
export function getAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  return read(window.sessionStorage) ?? read(window.localStorage);
}

/**
 * Legt de herkomst vast bij de eerste paginaweergave. Een nieuwe advertentie-
 * klik (gclid in de URL) overschrijft altijd een eerdere herkomst, zodat de
 * laatste betaalde klik telt — net zoals Google Ads zelf meet.
 */
export function captureAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const pick = (name: string) => params.get(name) ?? undefined;
  const gclid = pick("gclid");
  const gbraid = pick("gbraid");
  const wbraid = pick("wbraid");
  const existing = getAttribution();
  const hasAdClick = Boolean(gclid || gbraid || wbraid);
  if (existing && !hasAdClick) return existing;

  const next: Attribution = {
    ...(gclid ? { gclid } : {}),
    ...(gbraid ? { gbraid } : {}),
    ...(wbraid ? { wbraid } : {}),
    ...(pick("utm_source") ? { utm_source: pick("utm_source")! } : {}),
    ...(pick("utm_medium") ? { utm_medium: pick("utm_medium")! } : {}),
    ...(pick("utm_campaign") ? { utm_campaign: pick("utm_campaign")! } : {}),
    ...(document.referrer ? { referrer: document.referrer } : {}),
    landing_page: window.location.pathname,
    first_seen: new Date().toISOString(),
  };
  write(next);
  return next;
}

/** Kwam deze bezoeker (voor zover meetbaar) via een Google Ads-advertentie? */
export function cameFromGoogleAds(): boolean {
  const a = getAttribution();
  if (!a) return false;
  if (a.gclid || a.gbraid || a.wbraid) return true;
  return a.utm_medium === "cpc" || a.utm_medium === "ppc" || a.utm_source === "google_ads";
}
